<template>
    <v-container fluid grid-list-md style="width: 95%;">
        <div class="d-flex flex-wrap">
            <h1>({{ hotelcode }}) {{ hotelname }} Batch Loader</h1>
            <ies-hotel-quick-links :pageid="hotelkey" />
        </div>
        
        <div>Rates are in {{ currencycode }} per room/unit per night{{ taxSummary }}</div>

        <ies-hotel-planner-month-controls
                    v-if="responseData"
                    class="d-none d-lg-block" 
                    :hotelkey="hotelkey" 
                    :rateColumns="rateColumns"
                    :selectedDate.sync="dateFrom"
                    :isPlannerPage="false"
                    :resetMonthControls="resetMonthControls"
                    @update:resetMonthControls="handleMonthControlsReset"
                    />

        <form id="batchForm" class="form-horizontal" role="form" method="POST" action="">

            <div class="d-flex flex-wrap">
                    <div class="form-section mr-2 mb-2" v-if="dateRanges.length > 0" style="min-width:100%;">
                        <h3 class="red-dark white--text pl-2">1.) Select Dates</h3>

                        <ies-hotel-batch-loader-date-selector
                            :dateRanges="dateRanges"
                            :selectedDays="selectedDays"
                            @update-date-ranges="updateDateRanges"
                            @update-selected-days="updateSelectedDays"
                        />
                    </div>

                    <div class="form-section mr-2 mb-2">
                        <h3 class="red-dark white--text pl-2">2.) Select Fields to load / update</h3>
                        
                        <ies-hotel-batch-loader-field-selector
                            v-if="responseData"
                            :payload="fieldSelectorPayload"
                            @toggle-show-availability="toggleShowAvailability"
                            @toggle-closed="toggleClosed"
                            @toggle-all-hotel-rooms="toggleAllHotelRooms"
                            @toggle-hotel-room="toggleHotelRoom"
                            @toggle-rate-type="toggleRateType"
                            @toggle-additional-rate="toggleAdditionalRate"
                            @toggle-restriction="toggleRestriction"
                        />
                    </div>

                    <div class="form-section mr-2 mb-2" v-if="responseData && !responseData.isChannelManaged">
                        <h3 class="red-dark white--text pl-2">3.) Enter values</h3>
                        <ies-hotel-batch-loader-submit-form
                            :payload="submitFormPayload"
                            @update-error-message="updateErrorMessage"
                            @save-success="handleSaveSuccess"
                            />    
                        
                        <ies-hotel-batch-loader-value-selector
                            
                            :payload="valueSelectorPayload"
                            @update-availability="updateAvailability"
                            @update-standard-rate="updateStandardRate"
                            @update-additional-rate="updateAdditionalRate"
                            @update-restriction="updateRestriction"
                            @update-universal-rate="updateUniversalRate"
                            @values-reset="handleValuesReset"
                            />

                        <ies-hotel-batch-loader-submit-form
                            :payload="submitFormPayload"
                            @update-error-message="updateErrorMessage"
                            @save-success="handleSaveSuccess"
                            />     
                    </div>
                </div>
            </form>
    </v-container>
</template>

<script>
/**
* A Vue component for the user to batch update hotel availability and rates for a given date range
*
*/
import axios from 'axios';

export default {
    name: 'hotel-batch-loader',
    props: ['hotelkey', 'hotelname', 'hotelcode', 'currencycode'],
    data () {        
        return {
            responseData: null,           
            errorMessage: '',
            resetValues: false,
            resetMonthControls: false,
            rangeHasClosedDates: false,
            rangeClosedDatesFrom: null,
            dateRanges: [],
            dateFrom: new Date(),
            selectedDays: [1,2,3,4,5,6,7],
            selectAvailability: false,
            selectClosed: false,
            selectedHotelRooms: [],
            selectedRateTypes: [],
            selectedAdditionalRates: [],
            selectedRestrictions: [],
            newAvailabilities: {},
            newStandardRates: {},
            newAdditionalRates: {},
            newRestrictions: {},
            newUniversalRates: {},
        }
    },

    methods: {
        /**
         * @see data allFacilities
         */
        loadData(){
            axios.get('/api/hotel-batch-loader', {'params': { 'hotelKey': this.hotelkey} } )
                .then(function(response){
                    this.dateRanges = response.data.dateRanges;
                    this.selectedDays = response.data.selectedDays;
                    this.responseData = response.data;
                    this.toggleAllHotelRooms();

                    this.initData();
                    this.checkPropertyClosed();
                }
                .bind(this));
        },

        initData(){
            this.initNewAvailabilities();
            this.initNewStandardRates();
            this.initNewAdditionalRates();
            this.initNewRestrictions();
        },

        initNewAvailabilities(){
            this.responseData.hotelRooms.forEach(hotelRoom => {
                this.$set(this.newAvailabilities, hotelRoom.hotelRoomKey, {
                    'status_key': null,
                    'quantity': ''
                });
            });
        },

        initNewStandardRates(){
            this.responseData.hotelRooms.forEach(hotelRoom => {
                this.$set(this.newStandardRates, hotelRoom.hotelRoomKey, {});

                this.responseData.rateTypes.forEach(rateType => {
                    this.$set(this.newStandardRates[hotelRoom.hotelRoomKey], rateType.rateTypeKey, {
                        'rate_standard': null,
                        'rate_status': null
                    });
                });
            });
        },

        initNewAdditionalRates(){
            this.responseData.hotelRooms.forEach(hotelRoom => {
                this.$set(this.newAdditionalRates, hotelRoom.hotelRoomKey, {});

                this.responseData.rateTypes.forEach(rateType => {
                    this.$set(this.newAdditionalRates[hotelRoom.hotelRoomKey], rateType.rateTypeKey, {});
                });
            });
        },

        initNewRestrictions(){
            let newRestrictions = {};

            this.responseData.hotelRooms.forEach(hotelRoom => {
                newRestrictions[hotelRoom.hotelRoomKey] = {};

                this.responseData.rateTypes.forEach(rateType => {
                    newRestrictions[hotelRoom.hotelRoomKey][rateType.rateTypeKey] = {};
                });
            });

            this.$set(this, 'newRestrictions', newRestrictions);
        },

        /**
         * @param {array} dateRanges - An array of date ranges to load availability and rates for
         * 
         * Updates the date ranges in the component and persists them to the session
         */
        updateDateRanges(dateRanges){
            this.dateRanges = dateRanges;

            axios.post('/api/hotel-batch-loader/remember-dates', {'hotelKey': this.hotelkey, 'dateRanges': dateRanges});

            this.checkPropertyClosed();
        },

        updateSelectedDays(selectedDays){
            this.selectedDays = selectedDays;

            axios.post('/api/hotel-batch-loader/remember-days', {'hotelKey': this.hotelkey, 'selectedDays': selectedDays});

            this.checkPropertyClosed();
        },

        checkPropertyClosed(){
            axios.get('/api/hotel-batch-loader/check-property-closed', {'params': {'hotelKey': this.hotelkey, 'dateRanges': this.dateRanges, 'days': this.selectedDays}})
                .then(function(response){
                    this.rangeHasClosedDates = response.data.closed;
                    this.rangeClosedDatesFrom = response.data.date;
                }
                .bind(this));
        },

        toggleClosed(){
            this.selectClosed = !this.selectClosed;
        },
        
        toggleShowAvailability(){
            this.selectAvailability = !this.selectAvailability;
        },

        toggleAllHotelRooms(){
            if (this.selectedHotelRooms.length == this.hotelRooms.length){
                this.selectedHotelRooms = [];
                return;
            }

            this.selectedHotelRooms = [];
            
            for (let i = 0; i < this.hotelRooms.length; i++){
                this.selectedHotelRooms.push(this.hotelRooms[i].hotelRoomKey);
            }  
        },

        toggleHotelRoom(hotelRoomKey){
            if (this.selectedHotelRooms.includes(hotelRoomKey)){
                this.selectedHotelRooms.splice(this.selectedHotelRooms.indexOf(hotelRoomKey), 1);
            } else {
                this.selectedHotelRooms.push(hotelRoomKey);
            }
        },

        toggleRateType(rateTypeKey){
            if (this.selectedRateTypes.includes(rateTypeKey)){
                this.selectedRateTypes.splice(this.selectedRateTypes.indexOf(rateTypeKey), 1);
            } else {
                this.selectedRateTypes.push(rateTypeKey);
            }
        },

        toggleAdditionalRate(additionalRate){
            if (this.selectedAdditionalRates.includes(additionalRate)){
                this.selectedAdditionalRates.splice(this.selectedAdditionalRates.indexOf(additionalRate), 1);
            } else {
                this.selectedAdditionalRates.push(additionalRate);
            }
        },

        toggleRestriction(restriction){
            if (this.selectedRestrictions.includes(restriction)){
                this.selectedRestrictions.splice(this.selectedRestrictions.indexOf(restriction), 1);
            } else {
                this.selectedRestrictions.push(restriction);
            }
        },

        updateAvailability(data){
            this.$set(this.newAvailabilities[data.hotelRoomKey], data.column, data.value);
        },

        updateStandardRate(data){
            this.$set(this.newStandardRates[data.hotelRoomKey][data.rateTypeKey], data.column, data.value);
        },

        updateAdditionalRate(data){
            this.$set(this.newAdditionalRates[data.hotelRoomKey][data.rateTypeKey], data.column, data.value);
        },

        updateRestriction(data){
            this.$set(this.newRestrictions[data.hotelRoomKey][data.rateTypeKey], data.column, data.value);
        },

        updateUniversalRate(data){
            this.$set(this.newUniversalRates, data.column, data.value);
        },

        updateErrorMessage(message){
            this.errorMessage = message;
        },

        handleValuesReset(){
            this.resetValues = false;
        },

        handleMonthControlsReset(){
            this.resetMonthControls = false;
        },

        handleSaveSuccess(){
            this.initData();
            this.resetValues = true;
            this.resetMonthControls = true;
            this.checkPropertyClosed();
        }
    },

    mounted() {
        this.loadData();
    },

    computed: {
        submitFormPayload() {
            return {
                'hotelKey': this.hotelkey,
                'availabilities': this.newAvailabilities,
                'standardRates': this.newStandardRatesFiltered,
                'additionalRates': this.newAdditionalRatesFiltered,
                'restrictions': this.newRestrictionsFiltered,
                'universalRates': this.newUniversalRates,
                'wholePropertyClosed': this.selectClosed,
                'dateRanges': this.dateRanges,
                'days': this.selectedDays,
            }
        },

        fieldSelectorPayload() {
            return {
                'selectClosed': this.selectClosed,
                'hotelRooms': this.hotelRooms,
                'rateTypes': this.rateTypes,
                'additionalRates': this.additionalRates,
                'restrictions': this.restrictions,
                'selectedHotelRooms': this.selectedHotelRooms
            }
        },
        
        valueSelectorPayload() {
            return {
                'dateRanges': this.dateRanges,
                'selectedDays': this.selectedDays,
                'rateTypes': this.rateTypes,
                'hotelRooms': this.hotelRooms,
                'restrictions': this.restrictions,
                'additionalRates': this.additionalRates,
                'universalRates': this.universalRates,
                'selectedHotelRooms': this.selectedHotelRooms,
                'selectedRateTypes': this.selectedRateTypes,
                'selectedAdditionalRates': this.selectedAdditionalRates,
                'selectedRestrictions': this.selectedRestrictions,
                'selectClosed': this.selectClosed,
                'selectAvailability': this.selectAvailability,
                'errorMessage': this.errorMessage,
                'resetValues' : this.resetValues,
                'rangeHasClosedDates': this.rangeHasClosedDates,
                'rangeClosedDatesFrom': this.rangeClosedDatesFrom
            }    
        },

        rateColumns : function(){
            let columns = [];

            if (!this.responseData ) {
                return columns;
            }

            this.responseData.additionalRates.forEach(rateOption => {
                if (['rate_standard', 'rate_single'].includes(rateOption.field) || !this.universalRates.length){
                    columns.push(rateOption.field);
                } 
            });

            return columns;
        },

        rateTypes() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.rateTypes == undefined) {
                return [];
            } 
            
            return this.responseData.rateTypes;
        },

        hotelRooms() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.hotelRooms == undefined) {
                return [];
            }

            return this.responseData.hotelRooms;
        },

        restrictions() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.restrictions == undefined) {
                return [];
            }

            return this.responseData.restrictions;
        },

        additionalRates() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.additionalRates == undefined) {
                return [];
            }

            return this.responseData.additionalRates;
        },

        universalRates() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.universalRates == undefined) {
                return [];
            }

            return this.responseData.universalRates;
        },

        taxSummary() {
            if (!this.responseData) {
                return '';
            }

            if (this.responseData.taxSummary == undefined) {
                return '';
            }

            return this.responseData.taxSummary;
        },

        availabilityStatuses() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.availabilityStatuses == undefined) {
                return [];
            }

            return this.responseData.availabilityStatuses;
        },

        rateStatuses() {
            if (!this.responseData) {
                return [];
            }

            if (this.responseData.rateStatuses == undefined) {
                return [];
            }

            return this.responseData.rateStatuses;
        },

        newAvailabilitiesFiltered() {
            let filteredAvailabilities = {};

            for (let [roomKey, values] of Object.entries(this.newAvailabilities)) {
                filteredAvailabilities[roomKey] = {'quantity': null,'availability_status': null};
                if (this.selectedHotelRooms.includes(roomKey)) {
                    filteredAvailabilities[roomKey] = values;
                }
            }

            return filteredAvailabilities;
        },

        newStandardRatesFiltered() {
            let filteredStandardRates = {};

            for (let [roomKey, rates] of Object.entries(this.newStandardRates)) {
                filteredStandardRates[roomKey] = {};
                for (let [rateKey, rate] of Object.entries(rates)) {
                    filteredStandardRates[roomKey][rateKey] = {'rate_standard': null, 'rate_status': null};
                    if (this.selectedRateTypes.includes(Number(rateKey)) && this.selectedHotelRooms.includes(Number(roomKey))) {
                        filteredStandardRates[roomKey][rateKey] = rate;
                    } 
                }
            }

            return filteredStandardRates;
        },

        newAdditionalRatesFiltered() {
            let filteredAdditionalRates = {};

            for (let [roomKey, rates] of Object.entries(this.newAdditionalRates)) {
                filteredAdditionalRates[roomKey] = {};
                for (let [rateKey, rate] of Object.entries(rates)) {
                    filteredAdditionalRates[roomKey][rateKey] = {};
                    
                    if (!this.selectedRateTypes.includes(Number(rateKey)) || !this.selectedHotelRooms.includes(Number(roomKey))) {
                        continue;
                    }
                    
                    for (let [additionalRateKey, additionalRate] of Object.entries(rate)) {
                        if (this.selectedAdditionalRates.includes(additionalRateKey)) {
                            filteredAdditionalRates[roomKey][rateKey][additionalRateKey] = additionalRate;
                        }
                    }
                }
            }

            return filteredAdditionalRates;
        },

        newRestrictionsFiltered() {
            let filteredRestrictions = {};

            for (let [roomKey, rates] of Object.entries(this.newRestrictions)) {
                filteredRestrictions[roomKey] = {};
                for (let [rateKey, restrictions] of Object.entries(rates)) {
                    filteredRestrictions[roomKey][rateKey] = {};

                    if (!this.selectedRateTypes.includes(Number(rateKey)) || !this.selectedHotelRooms.includes(Number(roomKey))) {
                        continue;
                    }

                    for (let [restrictionKey, restriction] of Object.entries(restrictions)) {
                        if (this.selectedRestrictions.includes(restrictionKey)) {
                            filteredRestrictions[roomKey][rateKey][restrictionKey] = restriction;
                        }
                    }
                }
            }

            return filteredRestrictions;
        },
    },

    watch: 
    {
        dateRanges: function() {
            
        }         
    }
}
</script>

<style>

    .date-select {
        max-width: 100px;
    }
    .red-dark {
        background-color: #B71C1C;
    }

    .form-section {
        border: 1px solid black;
        background-color: #F2F2F2;
        height: fit-content;
        padding-bottom: 10px;
        min-width: 33vw;
        box-sizing: border-box;
    }

    .room-available {
    background-color: #C1CD23;
    }

    .room-on-request {
    background-color: yellow;
    }

    .room-unavailable {
    background-color: #B71C1C;
    }

    .spin {
        animation: spin 1s infinite linear;
    }

    .room-name {
        background-color: #ffc107;
        border: 1px solid black;    
    }

    .darker-grey {
        background-color: rgb(100, 100, 100);
    }

    .value {
        flex : 1 1 300px !important;
        min-height: 32px;
        max-width: 250px;
    }

    .icon-container {
        display: inline-block;
        width: 250px;
    }

    @keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(-360deg);
    }
}
</style>
